/* Admin → Inbox-Plus → SMS (v5.5)
 * Two-way SMS via Twilio with conversation list + status indicator.
 *
 * If Twilio is not configured, sends still work in stub mode (returns
 * "Twilio not configured") and the inbound webhook still records incoming
 * messages — the UI shows a clear "needs credentials" banner.
 */
import { useEffect, useState } from "react";
import { Send, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function AdminSms() {
  const [status, setStatus] = useState(null);
  const [convos, setConvos] = useState([]);
  const [activePhone, setActivePhone] = useState("");
  const [messages, setMessages] = useState([]);
  const [optedOut, setOptedOut] = useState(false);
  const [draft, setDraft] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/sms/status");
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const loadConvos = async () => {
    try {
      const { data } = await api.get("/admin/sms/conversations");
      setConvos(data.conversations || []);
    } catch {
      // ignore
    }
  };

  const loadMessages = async (phone) => {
    if (!phone) {
      setMessages([]);
      setActivePhone("");
      return;
    }
    try {
      const { data } = await api.get(`/admin/sms/conversations/${encodeURIComponent(phone)}`);
      setMessages(data.messages || []);
      setOptedOut(!!data.opted_out);
      setActivePhone(data.phone || phone);
      // mark inbound read
      api.post(`/admin/sms/conversations/${encodeURIComponent(data.phone || phone)}/read`).catch(() => {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadStatus();
    loadConvos();
    const i = setInterval(loadConvos, 15000);
    return () => clearInterval(i);
  }, []);

  const send = async (e) => {
    e?.preventDefault?.();
    const to = activePhone || newPhone;
    if (!to || !draft.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/sms/send", {
        to,
        body: draft,
      });
      if (data.ok) {
        setDraft("");
        await loadMessages(to);
        await loadConvos();
      } else {
        setError(data.error || "Send failed.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sms">
      <header>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Inbox-Plus</div>
        <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">SMS Conversations</h1>
        <p className="text-[var(--ssc-ink-2)] mt-2 max-w-3xl">
          Two-way SMS via Twilio. Customer phone numbers are auto-matched to
          recent leads. STOP/START/HELP keywords are handled automatically and
          opted-out numbers are blocked from outbound sends.
        </p>
      </header>

      {status && !status.configured && (
        <div
          className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex gap-2"
          data-testid="admin-sms-not-configured"
        >
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div>
            <strong>SMS is not yet activated.</strong> {status.instructions}
            Inbound webhook + conversation history work today; outbound sends
            will return an error until credentials are added.
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2" data-testid="admin-sms-error">{error}</div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        {/* Conversation list */}
        <aside className="rounded-xl border border-[var(--ssc-line)] bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--ssc-line)] flex justify-between items-center">
            <div className="font-extrabold text-[var(--ssc-navy)]">Conversations</div>
            <span className="text-xs text-[var(--ssc-ink-2)]">{convos.length}</span>
          </div>
          <ul className="max-h-[600px] overflow-y-auto" data-testid="admin-sms-convo-list">
            {convos.length === 0 && (
              <li className="px-4 py-6 text-sm text-[var(--ssc-ink-2)] text-center">
                No conversations yet.
              </li>
            )}
            {convos.map((c) => (
              <li key={c.phone} className={`border-b border-[var(--ssc-line)]/50 ${activePhone === c.phone ? "bg-blue-50" : ""}`}>
                <button
                  type="button"
                  onClick={() => loadMessages(c.phone)}
                  className="w-full text-left px-4 py-3 hover:bg-[var(--ssc-paper)]"
                  data-testid={`admin-sms-convo-${c.phone}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-bold text-[var(--ssc-ink)] truncate">
                      {c.matched_customer_name || c.phone}
                    </div>
                    {c.unread_count > 0 && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[var(--ssc-blue)] text-white">{c.unread_count}</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ssc-ink-2)] truncate">{c.last_body}</div>
                  <div className="text-[10px] text-[var(--ssc-ink-2)] mt-0.5">{c.last_at?.slice(0, 19).replace("T", " ")}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Active conversation */}
        <section className="rounded-xl border border-[var(--ssc-line)] bg-white shadow-sm overflow-hidden flex flex-col min-h-[600px]">
          <div className="px-4 py-3 border-b border-[var(--ssc-line)]">
            {activePhone ? (
              <>
                <div className="font-extrabold text-[var(--ssc-navy)]">{activePhone}</div>
                {optedOut && (
                  <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700 mt-1 inline-block">
                    Opted out — outbound blocked
                  </span>
                )}
              </>
            ) : (
              <div className="text-sm text-[var(--ssc-ink-2)]">Select a conversation or start a new one</div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[var(--ssc-paper)]/30" data-testid="admin-sms-message-list">
            {messages.length === 0 && (
              <p className="text-sm text-center text-[var(--ssc-ink-2)] py-6">No messages yet.</p>
            )}
            {messages.map((m, i) => (
              <div
                key={m.sid || `${m.received_at || m.sent_at}-${i}`}
                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  m.direction === "outbound"
                    ? "ml-auto bg-[var(--ssc-blue)] text-white"
                    : "mr-auto bg-white border border-[var(--ssc-line)] text-[var(--ssc-ink)]"
                }`}
                data-testid={`admin-sms-msg-${i}`}
              >
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-white/70" : "text-[var(--ssc-ink-2)]"}`}>
                  {(m.received_at || m.sent_at || "").slice(0, 19).replace("T", " ")}
                  {m.status === "failed" && " · failed"}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-[var(--ssc-line)] p-3 flex flex-col gap-2 bg-white">
            {!activePhone && (
              <input
                type="tel"
                placeholder="To phone (e.g. 614-535-8673)"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                className="ssc-input"
                data-testid="admin-sms-new-phone"
              />
            )}
            <div className="flex gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type your message..."
                className="ssc-input flex-1 min-h-[40px]"
                rows={2}
                data-testid="admin-sms-draft"
              />
              <button
                type="submit"
                disabled={busy || (!draft.trim()) || optedOut}
                className="ssc-btn ssc-btn-primary"
                data-testid="admin-sms-send"
              >
                <Send size={16} /> Send
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
